import React, { useState } from 'react';
import { Form, Input, Select } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useSignUp } from '../context/SignUpContext';
import StepNavigation from '../components/StepNavigation';
import { BRAZILIAN_STATES } from '../../../../api/signup';

const StepAddress = () => {
  const [form] = Form.useForm();
  const { formData, updatePersonAddress, nextStep, prevStep } = useSignUp();
  const [loadingCep, setLoadingCep] = useState(false);

  // Format CEP as user types
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const handleCEPChange = async (e) => {
    const formatted = formatCEP(e.target.value);
    form.setFieldValue('postal_code', formatted);
    updatePersonAddress('postal_code', formatted.replace(/\D/g, ''));

    // Auto-fill address from CEP
    const numbers = formatted.replace(/\D/g, '');
    if (numbers.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${numbers}/json/`);
        const data = await response.json();
        if (!data.erro) {
          form.setFieldsValue({
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf,
          });
          updatePersonAddress('street', data.logradouro);
          updatePersonAddress('neighborhood', data.bairro);
          updatePersonAddress('city', data.localidade);
          updatePersonAddress('state', data.uf);
        }
      } catch (err) {
        // CEP lookup failed, user can fill manually
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      nextStep();
    } catch (err) {
      // Validation failed
    }
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>Endereço</h2>
        <p>Informe o endereço do responsável pela conta.</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          postal_code: formData.person.address.postal_code ? formatCEP(formData.person.address.postal_code) : '',
          street: formData.person.address.street,
          number: formData.person.address.number,
          complement: formData.person.address.complement,
          neighborhood: formData.person.address.neighborhood,
          city: formData.person.address.city,
          state: formData.person.address.state,
        }}
        requiredMark={false}
      >
        <Form.Item
          name="postal_code"
          label="CEP"
          rules={[
            { required: true, message: 'Digite o CEP' },
            {
              validator: (_, value) => {
                const numbers = value?.replace(/\D/g, '') || '';
                if (numbers.length !== 8) {
                  return Promise.reject('CEP deve ter 8 dígitos');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            prefix={<HomeOutlined />}
            placeholder="00000-000"
            size="large"
            onChange={handleCEPChange}
            maxLength={9}
            loading={loadingCep}
          />
        </Form.Item>

        <Form.Item
          name="street"
          label="Rua/Avenida"
          rules={[{ required: true, message: 'Digite o nome da rua' }]}
        >
          <Input
            placeholder="Nome da rua ou avenida"
            size="large"
            onChange={(e) => updatePersonAddress('street', e.target.value)}
          />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="number"
            label="Número"
            className="form-item-small"
          >
            <Input
              placeholder="123"
              size="large"
              onChange={(e) => updatePersonAddress('number', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="complement"
            label="Complemento"
            className="form-item-large"
          >
            <Input
              placeholder="Apto, sala, etc. (opcional)"
              size="large"
              onChange={(e) => updatePersonAddress('complement', e.target.value)}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="neighborhood"
          label="Bairro"
          rules={[{ required: true, message: 'Digite o bairro' }]}
        >
          <Input
            placeholder="Nome do bairro"
            size="large"
            onChange={(e) => updatePersonAddress('neighborhood', e.target.value)}
          />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="city"
            label="Cidade"
            className="form-item-large"
            rules={[{ required: true, message: 'Digite a cidade' }]}
          >
            <Input
              placeholder="Nome da cidade"
              size="large"
              onChange={(e) => updatePersonAddress('city', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="state"
            label="Estado"
            className="form-item-small"
            rules={[{ required: true, message: 'Selecione o estado' }]}
          >
            <Select
              placeholder="UF"
              size="large"
              showSearch
              optionFilterProp="children"
              onChange={(value) => updatePersonAddress('state', value)}
            >
              {BRAZILIAN_STATES.map((state) => (
                <Select.Option key={state.code} value={state.code}>
                  {state.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>

      <StepNavigation
        onNext={handleNext}
        onBack={prevStep}
      />
    </div>
  );
};

export default StepAddress;
